import { useCallback, useMemo } from 'react';

import { useStoreAvailabilityFns } from 'features/store/hooks/use-store-availability-fns';
import { IStore } from 'features/store/pages/store-locator-experiment/ducks/stores';
import { isMobileOrderingAvailableExperiment } from 'features/store/utils';

import { IRestaurant } from '@rbi-ctg/store';
import { useConfigValue } from 'hooks/configs/use-config-value';
import useGoogleGeolocationLibrary from 'hooks/geolocation/use-google-geolocation-library';
import { deriveMarkerIcon } from 'hooks/use-map/icons';
import { IMarker, MarkerTypes } from 'hooks/use-map/types';
import { useGeolocation } from 'state/geolocation';
import { useLocale } from 'state/intl';
import { isValidPosition } from 'utils/geolocation';
import noop from 'utils/noop';

export interface IUseMarkersProps {
  favoritesStores?: IStore[];
  stores?: IStore[];
  onPress: (store: IStore) => void;
  selectedStoreId?: string | null;
  restaurantFocused: IStore | null;
}

const determineMarkerType = (options: {
  isSelected: boolean;
  isFocused: boolean;
  isStoreFavorite: boolean;
  isStoreAvailable: boolean;
}): MarkerTypes => {
  const { isSelected, isFocused, isStoreFavorite, isStoreAvailable } = options;

  if (isSelected) {
    if (isStoreAvailable) {
      if (isFocused) {
        return MarkerTypes.StoreOpenSelected;
      }
      return isStoreFavorite ? MarkerTypes.StoreFav : MarkerTypes.StoreOpen;
    }
    return isStoreFavorite
      ? MarkerTypes.StoreFavNotAvailable
      : MarkerTypes.StoreNotAvailableSelected;
  }

  if (isFocused) {
    return isStoreAvailable ? MarkerTypes.StoreOpenSelected : MarkerTypes.StoreNotAvailableSelected;
  }

  if (!isStoreAvailable) {
    return isStoreFavorite ? MarkerTypes.StoreFavNotAvailable : MarkerTypes.StoreNotAvailable;
  }

  return isStoreFavorite ? MarkerTypes.StoreFav : MarkerTypes.StoreOpen;
};

export const useMarkerGenerator = ({
  favoritesStores,
  stores,
  onPress,
  selectedStoreId,
  restaurantFocused,
}: IUseMarkersProps) => {
  const { libraryLoaded: libLoaded } = useGoogleGeolocationLibrary();
  const { isOpenForPickup } = useStoreAvailabilityFns();
  const updateMarkerType = useCallback((marker: IMarker, newType: MarkerTypes) => {
    if (marker.type !== newType) {
      marker.type = newType;
      marker.icon = deriveMarkerIcon(newType);
    }
    return marker;
  }, []);

  const {
    userCoordinates,
    manualCoordinates,
    isPermissionGranted,
    manualAddress,
  } = useGeolocation();

  const { region } = useLocale();

  const restaurantsConfig = useConfigValue({ key: 'restaurants', defaultValue: {} });

  const validMobileOrderingEnvs = useMemo(() => restaurantsConfig.validMobileOrderingEnvs ?? [], [
    restaurantsConfig.validMobileOrderingEnvs,
  ]);

  const favoriteStoresMap = useMemo(() => new Set(favoritesStores?.map(x => x._id)), [
    favoritesStores,
  ]);

  return useCallback((): IMarker[] => {
    if (!libLoaded) {
      return [];
    }

    const markers = (stores ?? []).reduce<IMarker[]>((markers, store) => {
      const id = store._id;
      const location = { lat: store.latitude, lng: store.longitude };

      if (!id || !isValidPosition(location)) {
        return markers;
      }

      const isSelected = store._id === selectedStoreId;
      const isFocused = store._id === restaurantFocused?._id;

      const isOpen = isOpenForPickup((store as unknown) as IRestaurant, store.localTime);

      const isCorrectRegion = !!store.country && store.country.toUpperCase().startsWith(region);

      /**
       * TODO: Should use restaurantCanBeSelected as it's more thorough.
       * Ideally this should be `isStoreAvailable = restaurantCanBeSelected && isCorrectRegion;`
       */
      const isStoreAvailable =
        isMobileOrderingAvailableExperiment(store, validMobileOrderingEnvs) &&
        isOpen &&
        isCorrectRegion;

      const markerType = determineMarkerType({
        isSelected,
        isFocused,
        isStoreFavorite: favoriteStoresMap.has(id),
        isStoreAvailable,
      });

      const marker = updateMarkerType(
        {
          id,
          type: MarkerTypes.StoreOpen,
          location,
          onPress: () => onPress(store),
          isSelected,
          isFocused,
          visible: true,
          preventMarkerPress: false,
          icon: deriveMarkerIcon(MarkerTypes.StoreOpen),
        },
        markerType
      );

      return markers.concat([marker]);
    }, []);

    if (userCoordinates || (manualCoordinates && manualAddress)) {
      const location = isPermissionGranted ? userCoordinates : manualCoordinates;

      if (!isValidPosition(location)) {
        return markers;
      }

      const isVisible =
        location.lat !== restaurantFocused?.longitude &&
        location.lat !== restaurantFocused?.latitude;

      markers.push({
        id: 'user',
        type: MarkerTypes.User,
        location,
        onPress: noop,
        isSelected: false,
        isFocused: false,
        visible: isVisible,
        preventMarkerPress: true,
        icon: deriveMarkerIcon(MarkerTypes.User),
      });
    }

    return markers.filter(marker => marker !== null);
  }, [
    libLoaded,
    stores,
    userCoordinates,
    manualCoordinates,
    manualAddress,
    selectedStoreId,
    restaurantFocused?._id,
    restaurantFocused?.longitude,
    restaurantFocused?.latitude,
    isOpenForPickup,
    region,
    validMobileOrderingEnvs,
    favoriteStoresMap,
    updateMarkerType,
    onPress,
    isPermissionGranted,
  ]);
};
